import type { NoyauInstrumentFingerprint } from '../schemas/analysis.js';
import {
  ANALYST_FUNCTIONS,
  ANALYST_FUNCTION_NAMES,
  ANALYST_LEVELS,
  ANALYST_LEVEL_LABELS,
} from '../schemas/analyst.js';
import type { MethodContent } from '../schemas/method.js';
import { NOYAUX, type NoyauCode, noyauOf } from '../schemas/noyau.js';
import {
  PROTOCOL_ANALYST_DEFAULT,
  type ProtocolAnalyst,
  type ProtocolStepDraft,
} from '../schemas/protocol.js';
import type { StyleServedAnswer } from './analyst-passes.js';
import { ANALYST_READING_TITLE, resolveAnalystReading } from './analyst-prompt.js';
import { noyauFunctionAt } from './levels.js';

/**
 * THE INSTRUMENT of the analyst in two passes (analyste.md §5.1, §7.2, issue
 * #196): the system block of each pass, the message of each, the generic
 * readings, and the fingerprint of the two systems together - one function each,
 * pure, in `shared`.
 *
 * ## Here, in `shared`, and not in the backend (N41)
 *
 * The server assembles the systems it serves with these functions, and the
 * Analyste section of the protocol editor draws « Ce que la passe 1 recevra » and
 * « Ce que la passe 2 recevra » with the very same ones (§3.4): the panel and the
 * served entry cannot part company. So each system is returned PART BY PART, every
 * part saying who wrote it - `source` - for the panel to grey what is written in
 * code, grey and mark with its version what Réglages > Méthode wrote, and frame in
 * violet what the protocol wrote.
 *
 * ## Four parts each, and the same four kinds
 *
 * | Part | Pass 1 | Pass 2 | Written by |
 * |---|---|---|---|
 * | `framing` | what the pass reads and says | what the pass reads and says | the code |
 * | `method` | « Les trois fonctions » | « Les six styles » | Réglages > Méthode, the version the session froze |
 * | `reading` | « Comment lire la matière » | « Comment lire le discours » | the protocol played |
 * | `contract` | the JSON of §5.2 | the JSON of §7.3 | the code: it is what Zod reads |
 *
 * The system is identical from one session to the next for a version of the
 * protocol and a version of Méthode - the second pass carries all SIX styles, and
 * it is the MESSAGE that names the candidates (K10) - so a provider's cache point
 * serves the sessions between them (N43). Nothing of the first pass's message
 * reaches the second (G14): no level, no part, no candidate but by its triplet.
 */

/**
 * The version of the ASSEMBLY written in code for the two passes (K11): `3`, the
 * one every call of the new analyst records as `promptVersion`. Bumped by hand by
 * the merge request that retouches a framing, a heading or a contract here -
 * never for a reading or a Méthode, which the fingerprint tells apart.
 */
export const ANALYST_TWO_PASS_PROMPT_VERSION = '3';

/** The headings the parts open with - in code: the protocol writes the body of a reading, not its title. */
export const ANALYST_FUNCTIONS_TITLE = '## Les trois fonctions';
export const ANALYST_STYLES_TITLE = '## Les six styles';
/** The reading of the first pass opens on the heading the reading of before #196 had. */
export const ANALYST_FUNCTIONS_READING_TITLE = ANALYST_READING_TITLE;
export const ANALYST_STYLE_READING_TITLE = '## Comment lire le discours';
const CONTRACT_TITLE = '## Ce que tu rends';

/* ------------------------------------------------------------- generic readings */

/**
 * The generic reading of the FIRST pass (P11, N34, K15): the template a protocol
 * created from now on is born with, and the one a team starts from. It says how
 * to read extracts grouped by objective, with nothing of any protocol in it - no
 * name, no token - so it fits the first protocol anyone creates.
 */
export const ANALYST_FUNCTIONS_READING_TEMPLATE = `La matière est faite d'extraits : les mots mêmes de la personne, rangés sous l'objectif de l'entretien auquel ils répondent. Le nom d'un objectif dit de quoi parlait l'entretien à ce moment-là ; il ne dit rien de la fonction de la personne.

- Lis chaque objectif pour lui-même : ce que la personne dit sur ce sujet précis, pas ce qu'elle dit ailleurs.
- Les questions de l'entretien ont orienté ce que la personne a dit : un thème que l'entretien a imposé n'est pas une préférence de la personne. Juge ce qu'elle en fait, pas le sujet dont on lui a parlé.
- Un choix entre deux options que l'interviewer a proposées dit peu de chose : cherche ce que la personne ajoute de son propre chef.
- Une idée posée comme la principale (« par-dessus tout », « avant tout », « ce qui compte le plus ») pèse plus qu'une idée citée en passant.
- Quand les extraits d'un objectif sont minces ou ambigus, répartis les parts sans en forcer une : trois parts proches valent mieux qu'un contraste inventé.`;

/**
 * The generic reading of the SECOND pass (P11, N34, K15): how to read the whole
 * of what the person answered, small talk included, to judge a style.
 */
export const ANALYST_STYLE_READING_TEMPLATE = `Tu lis tout ce que la personne a répondu pendant l'entretien, dans l'ordre, y compris les échanges d'accueil et de simple conversation : la façon dont elle parle en dit autant que ce dont elle parle.

- Juge le style, pas le sujet : les questions de l'entretien ont imposé des thèmes, et un thème imposé n'est pas un style.
- Cherche ce qui revient : un mot, un tour de phrase, une manière de raconter qui se retrouve à plusieurs endroits pèse plus qu'une phrase isolée.
- Le style se lit sur l'ensemble du discours, jamais réponse par réponse : une réponse courte ne suffit pas à conclure.
- Pour chaque noyau, cite les passages qui soutiennent son style et ceux qui le contredisent, mot pour mot.
- Quand le discours est mince ou ambigu, garde des notes modérées et proches les unes des autres plutôt que d'inventer un contraste.`;

/**
 * The Analyste section a protocol is CREATED with (K15): the three defaults of
 * the section and the two generic readings, and no level - the levels are set on
 * the objectives, and a protocol that has none is incomplete until they are
 * (§3.6). A duplication copies the section of its source instead.
 */
export const PROTOCOL_ANALYST_TEMPLATE: ProtocolAnalyst = {
  ...PROTOCOL_ANALYST_DEFAULT,
  functionsReading: ANALYST_FUNCTIONS_READING_TEMPLATE,
  styleReading: ANALYST_STYLE_READING_TEMPLATE,
};

/* ------------------------------------------------------------------- the parts */

/** Who wrote a part: the code, Réglages > Méthode, or the protocol played (§3.4). */
export type AnalystPassPartSource = 'code' | 'method' | 'protocol';

/** The four parts of a system, in the order it assembles them. */
export type AnalystPassPartKind = 'framing' | 'method' | 'reading' | 'contract';

/**
 * One part of a system block, as the panel « Ce que la passe N recevra » draws it:
 * who wrote it, the markdown heading it opens with, and what follows. Only the body
 * of `method` comes from Méthode and only the body of `reading` from the protocol.
 */
export type AnalystPassPart = {
  readonly kind: AnalystPassPartKind;
  readonly source: AnalystPassPartSource;
  /** The heading the part opens with - null for the framing, which has none. */
  readonly heading: string | null;
  readonly body: string;
};

/** One part as the analyst receives it: its heading, a blank line, then its body. */
export function analystPassPartText(part: AnalystPassPart): string {
  return part.heading === null ? part.body : `${part.heading}\n\n${part.body}`;
}

/** The parts joined by one blank line: THE system block. */
function systemOf(parts: readonly AnalystPassPart[]): string {
  return parts.map(analystPassPartText).join('\n\n');
}

/**
 * What a system is assembled from besides what Méthode says: the reading AS TYPED,
 * and the steps of the protocol that cite objectives in it. The reading's
 * `{{objectif:id}}` are resolved HERE, to the NAME the steps give that objective
 * (« (objectif retiré) » for a ghost), so the server - on the frozen steps - and
 * the panel - on the draft - cannot disagree on what is served (N50).
 */
export type AnalystPassInput = {
  readonly reading: string;
  readonly steps: readonly ProtocolStepDraft[];
};

/* ------------------------------------------------------------------- the first pass */

const FUNCTIONS_FRAMING = `Tu es l'analyste d'un dispositif d'entretien qui suit la méthode de Patrick Mathieu, la singularité trifonctionnelle. Tu ne parles jamais à la personne : tu lis des extraits d'un entretien qu'elle a eu, rangés par objectif, et tu dis, pour chaque objectif, la part de chacune des trois fonctions dans ce qu'elle y dit. Ta réponse est lue par un programme.

Un extrait est un passage des mots mêmes de la personne. Tu lis chaque objectif pour lui-même, sans chercher à quoi il servira ensuite : le programme en tire seul la suite.`;

const FUNCTIONS_CONTRACT = `Pour chaque objectif listé dans le message, une fois et une seule :
- « objectiveId » : l'identifiant de l'objectif, tel qu'il est donné.
- « shares » : la part de chacune des trois fonctions, « S » (Souverain), « G » (Guerrier) et « P » (Producteur) : trois entiers de 0 à 100 dont la somme vaut exactement 100. Les parts se partagent cent points : elles disent ce qui, dans les extraits de CET objectif, relève de chaque fonction.
- « justification » : une seule phrase, en français, qui dit ce qui, dans les extraits, porte ces parts.

Tu ne désignes aucun noyau : tu donnes les parts, et le programme en tire le reste.

Réponds UNIQUEMENT par un objet JSON, sans texte avant ni après, sans bloc de code, à ce format exact, avec chaque objectif une fois et une seule. Les 0 du gabarit sont des places à remplir, pas des réponses.

{
  "objectives": [
    { "objectiveId": "<identifiant>", "shares": { "S": 0, "G": 0, "P": 0 }, "justification": "<une phrase>" }
  ]
}`;

/** The three functions as the first pass reads them, in the order S, G, P: what each designates, then its words. */
function functionsBody(functions: MethodContent['functions']): string {
  return ANALYST_FUNCTIONS.map((fn) => {
    const entry = functions[fn];
    return `${ANALYST_FUNCTION_NAMES[fn]} (${fn})\nCe qu'elle désigne : ${entry.designates}\nMots et expressions de la langue parlée : ${entry.words}`;
  }).join('\n\n');
}

/**
 * The four parts of the system of the FIRST pass (§5.1): the framing and the
 * contract in code, « Les trois fonctions » from the version of Méthode, and
 * « Comment lire la matière » from the protocol's reading, resolved.
 */
export function functionsSystemParts(
  input: AnalystPassInput & { readonly functions: MethodContent['functions'] },
): readonly AnalystPassPart[] {
  return [
    { kind: 'framing', source: 'code', heading: null, body: FUNCTIONS_FRAMING },
    {
      kind: 'method',
      source: 'method',
      heading: ANALYST_FUNCTIONS_TITLE,
      body: functionsBody(input.functions),
    },
    {
      kind: 'reading',
      source: 'protocol',
      heading: ANALYST_FUNCTIONS_READING_TITLE,
      body: resolveAnalystReading(input.reading, input.steps),
    },
    { kind: 'contract', source: 'code', heading: CONTRACT_TITLE, body: FUNCTIONS_CONTRACT },
  ];
}

/** THE system block of the first pass: its four parts, separated by one blank line. */
export function functionsSystemPrompt(
  input: AnalystPassInput & { readonly functions: MethodContent['functions'] },
): string {
  return systemOf(functionsSystemParts(input));
}

/** One objective of the matter the first pass reads (§5.1): its identifier, its name and its live extracts. */
export type FunctionsMatterObjective = {
  readonly objectiveId: string;
  readonly name: string;
  readonly extracts: readonly string[];
};

/**
 * The MESSAGE of the first pass (§5.1): for each objective that carries a level -
 * the caller serves those only, in the order of the protocol, and leaves out one
 * with no live extract - its NAME, its IDENTIFIER and its extracts, each quoted on
 * its own line. Neither the level, nor the table of the six noyaux, nor a triplet
 * (G10): the first pass cannot reason backwards to a noyau.
 */
export function functionsMessage(matter: readonly FunctionsMatterObjective[]): string {
  const blocks = matter.map((objective) => {
    const lines = objective.extracts.map((extract) => `- « ${extract} »`);
    return `Objectif : ${objective.name} (identifiant : ${objective.objectiveId})\n${lines.join('\n')}`;
  });
  return `Voici la matière de l'entretien, objectif par objectif, dans l'ordre du protocole.\n\n${blocks.join('\n\n')}`;
}

/**
 * The correction sent after an answer of the first pass that could not be used
 * (§5.3): the retry of the repository - the answer as the analyst's own turn, this
 * as the user's. A call that brought nothing back is sent again as it was.
 */
export function functionsRetryMessage(expectedObjectiveIds: readonly string[]): string {
  return `Ta réponse n'a pas pu être utilisée : il faut un objet JSON seul, dont « objectives » porte exactement une entrée par objectif du message (${expectedObjectiveIds.join(', ')}), chacune avec ses trois parts entières de 0 à 100 dont la somme vaut exactement 100, et une phrase de justification. Rends la réponse entière, corrigée, au format exact du gabarit, sans texte avant ni après.`;
}

/* ------------------------------------------------------------------ the second pass */

const STYLE_FRAMING = `Tu es l'analyste d'un dispositif d'entretien qui suit la méthode de Patrick Mathieu, la singularité trifonctionnelle. Tu ne parles jamais à la personne : tu lis tout ce qu'elle a répondu pendant un entretien, et tu dis à quel point son discours a le style de chacun des noyaux candidats que le message te nomme. Ta réponse est lue par un programme, qui vérifie chaque passage que tu cites.

Le message porte les réponses de la personne, dans l'ordre, chacune sous le numéro de son tour. Les messages de l'interviewer ne te sont pas servis : seules comptent les paroles de la personne, y compris celles des échanges d'accueil et de simple conversation. Tu juges le style de son discours entier, jamais réponse par réponse.`;

const STYLE_CONTRACT = `Pour chaque noyau candidat nommé dans le message, une fois et une seule :
- « noyau » : le triplet du noyau, tel qu'il est donné.
- « score » : un entier de 0 à 100 : à quel point le discours de la personne a le style de ce noyau. 0, le discours le contredit ; 100, il en a pleinement le style. Les notes sont indépendantes : elles ne se partagent pas cent points, et deux noyaux peuvent recevoir la même note.
- « supporting » : au plus huit passages des réponses de la personne qui soutiennent ce style. Chacun est recopié MOT POUR MOT de ses réponses, sans corriger, sans résumer.
- « contradicting » : au plus huit passages qui le contredisent, recopiés de la même façon.
- « justification » : une seule phrase, en français, qui dit ce qui, dans le discours, porte la note.

Un programme cherche chaque passage dans les réponses de la personne : un passage qu'il ne retrouve pas est écarté. Tu ne classes pas les noyaux : tu les notes, et le programme classe.

Réponds UNIQUEMENT par un objet JSON, sans texte avant ni après, sans bloc de code, à ce format exact, avec chaque candidat une fois et une seule. Les 0 du gabarit sont des places à remplir, pas des réponses.

{
  "styles": [
    { "noyau": "<triplet>", "score": 0, "supporting": ["<mot pour mot>"], "contradicting": ["<mot pour mot>"], "justification": "<une phrase>" }
  ]
}`;

/** The six styles as the second pass reads them, in the order of the table: noyau, triplet, style, what recognises it, its typical words. */
function stylesBody(styles: MethodContent['styles']): string {
  return NOYAUX.map((noyau) => {
    const entry = styles[noyau.code];
    const levels = ANALYST_LEVELS.map(
      (level) =>
        `${ANALYST_LEVEL_LABELS[level]} ${ANALYST_FUNCTION_NAMES[noyauFunctionAt(noyau.code, level)]}`,
    ).join(', ');
    const lines = [
      `${noyau.code}, ${noyau.name} (${levels}) - style : ${entry.name}`,
      `Ce qui le reconnaît : ${entry.recognizes}`,
    ];
    if (entry.typicalWords !== '') lines.push(`Mots typiques : ${entry.typicalWords}`);
    return lines.join('\n');
  }).join('\n\n');
}

/**
 * The four parts of the system of the SECOND pass (§7.2): the framing and the
 * contract in code, « Les six styles » from the version of Méthode - all six, so
 * the system stays the same whoever the candidates are (K10) -, and « Comment lire
 * le discours » from the protocol's reading, resolved.
 */
export function styleSystemParts(
  input: AnalystPassInput & { readonly styles: MethodContent['styles'] },
): readonly AnalystPassPart[] {
  return [
    { kind: 'framing', source: 'code', heading: null, body: STYLE_FRAMING },
    {
      kind: 'method',
      source: 'method',
      heading: ANALYST_STYLES_TITLE,
      body: stylesBody(input.styles),
    },
    {
      kind: 'reading',
      source: 'protocol',
      heading: ANALYST_STYLE_READING_TITLE,
      body: resolveAnalystReading(input.reading, input.steps),
    },
    { kind: 'contract', source: 'code', heading: CONTRACT_TITLE, body: STYLE_CONTRACT },
  ];
}

/** THE system block of the second pass: its four parts, separated by one blank line. */
export function styleSystemPrompt(
  input: AnalystPassInput & { readonly styles: MethodContent['styles'] },
): string {
  return systemOf(styleSystemParts(input));
}

/**
 * The MESSAGE of the second pass (§7.2, K10): it names the CANDIDATES - code and
 * name - and then gives ALL the answers of the person on the protocol page, in
 * order, each under the number of its turn, small talk included. Neither the
 * messages of the AI, nor the farewell, nor the parts of the first pass, nor the
 * levels it fixed: the caller serves the answers, this lays them out.
 */
export function styleMessage(
  candidates: readonly NoyauCode[],
  answers: readonly StyleServedAnswer[],
): string {
  const named = candidates.map((code) => `- ${code} : ${noyauOf(code).name}`).join('\n');
  const replies = answers.map((answer) => `[t${answer.number}] ${answer.reply}`).join('\n\n');
  return `Les noyaux candidats, dont tu notes le style :\n${named}\n\nVoici tout ce que la personne a répondu pendant l'entretien, dans l'ordre, chaque réponse sous le numéro de son tour.\n\n${replies}`;
}

/** The correction sent after an answer of the second pass that could not be used (§7.5), as `functionsRetryMessage` does for the first. */
export function styleRetryMessage(candidates: readonly NoyauCode[]): string {
  return `Ta réponse n'a pas pu être utilisée : il faut un objet JSON seul, dont « styles » porte exactement une entrée par noyau candidat (${candidates.join(', ')}), chacune avec une note entière de 0 à 100, au plus huit passages pour et huit contre, et une phrase de justification. Rends la réponse entière, corrigée, au format exact du gabarit, sans texte avant ni après.`;
}

/* -------------------------------------------------------------- the fingerprint */

/** How many hexadecimal characters of the SHA-256 the fingerprint keeps (K11). */
export const INSTRUMENT_FINGERPRINT_LENGTH = 8;

/**
 * What the fingerprint of the INSTRUMENT is taken on (K11): the two systems, the
 * first pass's then the second's, joined by a blank line. It never depends on a
 * session - neither system names a candidate or a matter - so it is computed once,
 * before the first pass.
 */
export function instrumentFingerprintSource(systems: {
  readonly functions: string;
  readonly style: string;
}): string {
  return `${systems.functions}\n\n${systems.style}`;
}

/**
 * THE FINGERPRINT of the instrument (K11, §10): the first 8 hexadecimal
 * characters of the SHA-256 of `instrumentFingerprintSource`, in UTF-8. Two
 * determinations of one fingerprint received the same protocol section, the same
 * Méthode and the same code - what the measure of §16 groups on.
 *
 * `shared` holds no hash function: it goes to the browser, which has no
 * `node:crypto`, and the one place a digest is wanted is the server's (the régime
 * of `analystPromptFingerprint`). The caller hands in `sha256Hex`, the full
 * lower-case hexadecimal digest of a UTF-8 string, and gets back the shape
 * `noyauInstrumentFingerprintSchema` stores.
 */
export function instrumentFingerprintOf(
  systems: { readonly functions: string; readonly style: string },
  sha256Hex: (utf8: string) => string,
): NoyauInstrumentFingerprint {
  return sha256Hex(instrumentFingerprintSource(systems)).slice(0, INSTRUMENT_FINGERPRINT_LENGTH);
}
